import {
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Input,
  InputAdornment,
} from "@mui/material";
import _ from "lodash";
import React, { useEffect, useMemo, useState } from "react";
import { useStoreState, useStoreActions } from "../../models/RootStore";
import { t } from "ttag";
import { EditIcon } from "../../iconImages";
import { Delete } from "../../icons";
import ServiceNavigationBar from "../../widgets/Menu/ServiceNavigationBar";
import useStyles from "./DebugDevices.style";
import { Close, Search, ContentCopy } from "@mui/icons-material";
import AddEditDebugDevice from "./AddEditDebugDevice";

const DebugDevices = (props: any) => {
  const styles: any = useStyles();
  const allDebugDevices = useStoreState((state) => state.devices.allDebugDevices);
  const getAllDebugDevices = useStoreActions((actions) => actions.devices.getAllDebugDevices);
  const createDebugDevice = useStoreActions((actions) => actions.devices.createDebugDevice);
  const editDebugDevice = useStoreActions((actions) => actions.devices.editDebugDevice);
  const deleteDebugDevice = useStoreActions((actions) => actions.devices.deleteDebugDevice);

  useEffect(() => {
    getAllDebugDevices();
  }, []);

  const [searchValue, setSearchValue] = useState<any>("");
  const [openAddEdit, setOpenAddEdit] = useState<any>(null);

  const devicesList = useMemo(() => Object.values(allDebugDevices || {}), [allDebugDevices]);

  const filteredDevices = useMemo(() => devicesList.filter((device: any) => {
    if (!searchValue) return true;
    const serial = device.serial?.toUpperCase() || "";
    const search = searchValue.toUpperCase();
    return serial.indexOf(search) >= 0;
  }), [devicesList, searchValue]);

  const handleDelete = (id: string) => {
    deleteDebugDevice(id);
  };

  const handleEdit = (id: string) => {
    setOpenAddEdit(id);
  };

  const handleSave = (values: any) => {
    if (openAddEdit === "new") {
      createDebugDevice({ serial: values.serial, comment: values.comment });
    } else {
      editDebugDevice({ serial: openAddEdit, comment: values.comment });
    }
    setOpenAddEdit(null);
    getAllDebugDevices();
  };

  const handleCopyCommand = (command: string) => {
    navigator.clipboard.writeText(command);
  };

  return (
    <>
      <ServiceNavigationBar
        title={t`Debug Devices`}
        searchComponent={
          <Input
            placeholder={t`Search by serial...`}
            value={searchValue}
            onChange={(event: any) => setSearchValue(event.target.value)}
            disableUnderline={true}
            classes={{ root: styles.inputRoot }}
            startAdornment={
              <InputAdornment position="start" className={styles.searchAdorment}>
                <Search className={styles.searchIcon} />
              </InputAdornment>
            }
            endAdornment={
              searchValue && (
                <IconButton
                  onClick={() => setSearchValue("")}
                  className={styles.closeIconStyle}
                >
                  <Close />
                </IconButton>
              )
            }
          />
        }
        {...props}
      >
        <div className={styles.addButtonContainer}>
          <Button
            className={styles.buttonStyle}
            onClick={() => setOpenAddEdit("new")}
          >{t`ADD NEW DEVICE`}</Button>
        </div>
        {
          <>
            <Paper elevation={0} className={styles.paperPadding}>
              <TableContainer>
                <Table stickyHeader className="" aria-label="customized table">
                  <TableHead className={styles.tableHead}>
                    <TableRow>
                      <TableCell className={styles.headCells}>{t`SERIAL`}</TableCell>
                      <TableCell className={styles.headCells}>{t`COMMENT`}</TableCell>
                      <TableCell align="center" className={styles.headCells}>{t`COPY COMMAND`}</TableCell>
                      <TableCell align="center" className={styles.headCells}>{t`EDIT`}</TableCell>
                      <TableCell align="center" className={styles.headCells}>{t`DELETE`}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredDevices.map((device: any) => {
                      return (
                        <TableRow hover tabIndex={-1} key={device.serial}>
                          <TableCell
                            component="th"
                            scope="row"
                            align="left"
                            className={styles.rowCell}
                          >
                            {device.serial}
                          </TableCell>
                          <TableCell
                            component="th"
                            scope="row"
                            align="left"
                            className={styles.rowCell}
                          >
                            {device.comment}
                          </TableCell>
                          <TableCell
                            component="th"
                            scope="row"
                            align="center"
                            className={styles.rowCell}
                          >
                            <IconButton onClick={() => handleCopyCommand(device.command)}>
                              <ContentCopy />
                            </IconButton>
                          </TableCell>
                          <TableCell
                            component="th"
                            scope="row"
                            align="center"
                            className={styles.rowCell}
                          >
                            <IconButton onClick={() => handleEdit(device.serial)}>
                              <img src={EditIcon} alt="edit" />
                            </IconButton>
                          </TableCell>
                          <TableCell
                            component="th"
                            scope="row"
                            align="center"
                            className={styles.rowCell}
                          >
                            <IconButton
                              onClick={() => handleDelete(device.serial)}
                            >
                              <Delete />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
            {openAddEdit &&
              <AddEditDebugDevice
                isEdit={openAddEdit !== "new"}
                device={openAddEdit !== "new" ? allDebugDevices[openAddEdit] : null}
                setOpenAddEdit={setOpenAddEdit}
                handleSave={handleSave}
              />
            }
          </>
        }
      </ServiceNavigationBar>
    </>
  );
};

export default DebugDevices;
